import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { ScaffoldDialogService } from '@app/framework/dialog/scaffold-dialog.service';
import { IntegrationService } from '@services/integration.service';
import { IntegrationStatusModalComponent } from '../../generic/integration-status-modal/integration-status-modal.component';
import { NotificationsService } from '../../services/notifications.service';
import { ConfirmDialog } from '@generic/confirm-dialog/confirm-dialog.component';
import { filter, tap } from 'rxjs/operators';
import { EnvironmentService } from '@services/environment.service';
import { IntegrationPermissionsComponent } from '@generic/integration-status-modal/integration-permissions/integration-permissions.component';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from '@services/user.service';

@Component({
  selector: 'scaffold-integration-banner',
  templateUrl: './integration-banner.component.html',
  styleUrls: ['./integration-banner.component.scss'],
  standalone: false,
})
export class IntegrationBannerComponent implements OnInit, OnDestroy {
  public logo;
  public updateIntegrationPause$;
  public disableHover;
  public dialogRef$;
  public instanceType;
  public status;
  public requirements;
  @Input() canTogglePauseState: boolean = false;
  @Input() canRefreshState: boolean = false;
  @Input() showInfoIcon: boolean = false;
  @Input() fullClickInfo: boolean = false;
  @Input() showEditIcon: boolean = false;
  @Input() selectable: boolean = false;
  /** Current user role; edit affordance also requires manage_integrations when showEditIcon is true. */
  public roleData: any;
  private readonly destroy$ = new Subject<void>();
  private _integration: any;
  private integrationChanges$: Subscription;

  @Input()
  set integration(integration: any) {
    this._integration = integration;
    if (this.integrationChanges$) {
      this.integrationChanges$.unsubscribe();
    }
    this.integrationChanges$ = this.integrationService
      .watchIntegration$()
      .pipe(filter(changes => changes.id === integration.id))
      .subscribe(changes => {
        const combinedIntegration = { ...changes, ...changes.json_data };
        delete combinedIntegration.json_data;
        this._integration = combinedIntegration;
        if (integration.json_data?.lms_type === 'source') {
          integration.json_data.lms_type = 'canvas';
        }
      });
    if (integration.json_data?.lms_type === 'source') {
      console.log('Setting to canvas');
      integration.json_data.lms_type = 'canvas';
    }
    this.logo = this.getLogo(integration);
    if (
      integration.json_data?.status === 'processing' &&
      ['canvas', 'brightspace'].includes(integration.json_data?.lms_type)
    ) {
      console.log('Setting an error after 10 seconds');
      setTimeout(() => {
        integration.status = 'error';
        integration.message =
          'Error processing integration, refresh integration to recheck';
        integration.generatedState =
          'Error processing, refresh integration to recheck';
        integration.generatedStatus = 'error';
        integration.isRefreshing = false;
        console.log('Setting to error', integration);
      }, 10000);
    }
  }

  get integration(): any {
    return this._integration;
  }

  @Input() selected: boolean = false;
  @Input() syncDetails: boolean = false;
  @Output() infoClicked = new EventEmitter();
  @Output() editClicked = new EventEmitter();
  @Output() bannerClicked = new EventEmitter();

  constructor(
    private notifications: NotificationsService,
    public dialog: ScaffoldDialogService,
    private integrationService: IntegrationService,
    private environmentService: EnvironmentService,
    private userService: UserService
  ) {}

  get showEditIconVisible(): boolean {
    return this.showEditIcon && !!this.roleData?.manage_integrations;
  }

  ngOnInit() {
    this.userService
      .getRoleDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe(role => {
        this.roleData = role;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.integrationChanges$) {
      this.integrationChanges$.unsubscribe();
    }
  }

  getLogo(integration) {
    let type;
    if (integration.integration_type === 'dwh') {
      type = 'warehouse';
    } else if (integration.integration_type === 'live_event') {
      type = 'live_event';
    } else {
      type =
        integration.lms_type || integration.sis_type || integration.crm_type;
    }
    return `https://k16-hosted-lib.s3.us-east-2.amazonaws.com/${type}.png`;
  }

  getPlaceholderLogo() {
    return 'https://k16-hosted-lib.s3.us-east-2.amazonaws.com/placeholder.png';
  }

  getSystemType() {
    if (this.integration.integration_type === 'dwh') {
      return 'Warehouse';
    } else if (this.integration.integration_type === 'live_event') {
      return 'Live Event';
    } else {
      return this.integration[this.integration.integration_type + '_type'];
    }
  }

  getTypeColor(integration) {
    if (integration?.integration_type === 'lms') {
      return 'background-color: #3f8a94; color: white;';
    } else if (integration?.integration_type === 'sis') {
      return 'background-color: #f2862e; color: white;';
    } else if (integration?.integration_type === 'crm') {
      return 'background-color: #0077c8; color: white;';
    } else if (integration?.integration_type === 'dwh') {
      return 'background-color: #fcd21d; color: black;';
    } else if (integration?.integration_type === 'live_event') {
      return 'background-color:rgb(231, 17, 88); color: black;';
    }
    const neutralColor = 'background-color:rgb(139, 139, 139); color: white;';
    if (typeof integration?.integration_type !== 'string') {
      return neutralColor;
    }
    try {
      const hashCode = str => {
        return str
          .split('')
          .reduce(
            (prevHash, currVal) =>
              (prevHash << 5) - prevHash + currVal.charCodeAt(0),
            0
          );
      };

      const intToRGB = i => {
        const c = (i & 0x00ffffff).toString(16).toUpperCase();

        return '00000'.substring(0, 6 - c.length) + c;
      };

      const color = intToRGB(hashCode(integration.integration_type));
      const isDarkColor = color => {
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness < 128;
      };

      const textColor = isDarkColor(color) ? 'white' : 'black';
      return `background-color: #${color}; color: ${textColor};`;
    } catch (error) {
      return neutralColor;
    }
  }

  handleInfoClick($event) {
    $event.stopPropagation();
    this.openIntegrationStatusModal();
  }

  handleRefreshClick($event?) {
    $event?.stopPropagation();
    if (
      this.integration.isRefreshing ||
      this.integration?.status === 'processing'
    )
      return;
    this.openIntegrationStatusModal();
    this.integrationService
      .refreshIntegration$(this.integration)
      .subscribe(response => {
        let hasError =
          response.error || response.errorMessage || !response?.[0]?.id;

        if (hasError) {
          this.notifications.popNotification(
            '',
            'Error Refreshing Integration'
          );
        } else {
          this.notifications.popNotification(
            '',
            'Initiated Integration Refresh'
          );
        }
      });
  }

  handleBannerClicked() {
    if (this.fullClickInfo) {
      this.openIntegrationStatusModal();
    } else {
      this.bannerClicked.emit(this.integration);
    }
  }

  handleEditClick($event) {
    console.log('edit clicked');
    if ($event?.stopPropagation) {
      $event.stopPropagation();
    }

    this.editClicked.emit(this.integration);
  }

  togglePauseState($event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    let data = this.integration.paused
      ? {
          title: 'Resume Import',
          message:
            'This will allow courses from this integrations\n that are currently queued to continue to be processed.',
          confirmMessage: 'Resume',
          declineMessage: 'Cancel',
        }
      : {
          title: 'Pause Import',
          message:
            'Pausing imports for this integration will stop\n queued courses from being processed any further.',
          confirmMessage: 'Pause',
          declineMessage: 'Cancel',
        };

    let settings: any = {
      context: {
        data,
      },
    };

    this.dialogRef$ = this.dialog.open(ConfirmDialog, settings).onClose.pipe(
      tap(result => {
        if (result) {
          this.integrationService
            .updateIntegrationPause$(this.integration, this.integration.paused)
            .subscribe();
        }
      })
    );
  }

  openIntegrationStatusModal() {
    this.dialog.open(IntegrationStatusModalComponent, {
      context: { data: { integration: this.integration } },
    } as any);
  }

  openIntegrationPermissionsModal($event) {
    $event.stopPropagation();
    this.dialog.open(IntegrationPermissionsComponent, {
      context: { data: { integration: this.integration } },
    } as any);
  }
}
